/**
 * ویرایشگر متن مقاله
 * ===================
 * چرا ویرایشگرِ «مارک‌داون با نوار ابزار» و نه ویرایشگر HTML (WYSIWYG)؟
 *
 * ویرایشگر HTML، خروجی HTML می‌سازد و آن HTML باید در صفحه مقاله رندر شود.
 * تنها راهش `dangerouslySetInnerHTML` است — یعنی هر چیزی که در متن مقاله
 * بنشیند، در مرورگرِ کاربرِ واردشده اجرا می‌شود. در یک صرافی، این یعنی اگر
 * حساب یک نویسنده لو برود، مهاجم می‌تواند توکن نشست بازدیدکننده‌ها را بدزدد.
 *
 * اینجا متن به‌صورت مارک‌داون ذخیره می‌شود و `components/Markdown.tsx` آن را
 * به عناصر React تبدیل می‌کند — هیچ HTML خامی اجرا نمی‌شود.
 *
 * برای اینکه نویسنده مجبور نباشد نشانه‌گذاری حفظ کند، همه‌چیز با دکمه انجام
 * می‌شود: تیتر، پررنگ، فهرست، جدول، لینک، تصویر — به‌همراه پیش‌نمایش زنده،
 * میان‌بر صفحه‌کلید، و کشیدن‌ورهاکردن تصویر.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Code, Link2,
  Image as ImageIcon, Table, Minus, Eye, Columns2, Pencil, Undo2, Upload, Loader2,
} from 'lucide-react'
import Markdown from '../components/Markdown'
import { toast } from '../store'
import { fa } from '../lib/format'
import MediaPicker, { uploadImage } from './MediaPicker'

type Props = {
  value: string
  onChange: (v: string) => void
  minHeight?: number
}

type Action = {
  icon: any
  title: string
  key?: string          // میان‌بر با Ctrl/Cmd
  run: (t: Tools) => void
}

/** ابزارهای کار با متن انتخاب‌شده در textarea */
class Tools {
  constructor(
    private el: HTMLTextAreaElement,
    private set: (v: string, selStart?: number, selEnd?: number) => void,
  ) {}

  get value() { return this.el.value }
  get start() { return this.el.selectionStart }
  get end() { return this.el.selectionEnd }
  get selected() { return this.el.value.slice(this.start, this.end) }

  /** متن انتخاب‌شده را با چیز دیگری جایگزین می‌کند */
  replace(text: string, selectFrom?: number, selectTo?: number) {
    const v = this.value.slice(0, this.start) + text + this.value.slice(this.end)
    const base = this.start
    this.set(v,
      selectFrom === undefined ? base + text.length : base + selectFrom,
      selectTo === undefined ? base + text.length : base + selectTo)
  }

  /** دور متن انتخاب‌شده نشانه می‌گذارد؛ اگر چیزی انتخاب نشده، متن نمونه */
  wrap(mark: string, placeholder = 'متن') {
    const sel = this.selected
    if (sel) {
      // اگر همین الان دور متن همان نشانه هست، برمی‌داریم (کلید دوحالته)
      const before = this.value.slice(this.start - mark.length, this.start)
      const after = this.value.slice(this.end, this.end + mark.length)
      if (before === mark && after === mark) {
        const v = this.value.slice(0, this.start - mark.length) + sel +
                  this.value.slice(this.end + mark.length)
        this.set(v, this.start - mark.length, this.end - mark.length)
        return
      }
      this.replace(`${mark}${sel}${mark}`, mark.length, mark.length + sel.length)
    } else {
      this.replace(`${mark}${placeholder}${mark}`, mark.length, mark.length + placeholder.length)
    }
  }

  /** ابتدای هر خطِ انتخاب‌شده پیشوند می‌گذارد (تیتر، فهرست، نقل‌قول) */
  prefixLines(make: (line: string, i: number) => string) {
    const v = this.value
    let s = v.lastIndexOf('\n', this.start - 1) + 1
    let e = v.indexOf('\n', this.end)
    if (e === -1) e = v.length
    const block = v.slice(s, e) || ''
    const out = block.split('\n').map(make).join('\n')
    const next = v.slice(0, s) + out + v.slice(e)
    this.set(next, s, s + out.length)
  }

  /** بلوکی را در خط تازه می‌افزاید (جدول، خط جدا کننده، تصویر) */
  block(text: string) {
    const v = this.value
    const needsNlBefore = this.start > 0 && v[this.start - 1] !== '\n'
    const body = `${needsNlBefore ? '\n\n' : ''}${text}\n`
    this.replace(body)
  }
}

const toggleLine = (prefix: string) => (t: Tools) =>
  t.prefixLines((line) => (line.startsWith(prefix) ? line.slice(prefix.length) : prefix + line))

const ACTIONS: Action[][ ] = [
  [
    { icon: Bold, title: 'پررنگ  (Ctrl+B)', key: 'b', run: (t) => t.wrap('**') },
    { icon: Italic, title: 'مورب  (Ctrl+I)', key: 'i', run: (t) => t.wrap('*') },
    { icon: Code, title: 'کد', run: (t) => t.wrap('`', 'کد') },
  ],
  [
    { icon: Heading2, title: 'تیتر اصلی (H2)', run: toggleLine('## ') },
    { icon: Heading3, title: 'تیتر فرعی (H3)', run: toggleLine('### ') },
  ],
  [
    { icon: List, title: 'فهرست نقطه‌ای', run: toggleLine('- ') },
    {
      icon: ListOrdered, title: 'فهرست شماره‌دار',
      run: (t) => t.prefixLines((l, i) => (/^\d+\.\s/.test(l) ? l.replace(/^\d+\.\s/, '') : `${i + 1}. ${l}`)),
    },
    { icon: Quote, title: 'نقل‌قول', run: toggleLine('> ') },
  ],
]

export default function Editor({ value, onChange, minHeight = 420 }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [mode, setMode] = useState<'write' | 'split' | 'preview'>('split')
  const [picker, setPicker] = useState(false)
  const [busy, setBusy] = useState(false)
  const [drag, setDrag] = useState(false)
  const history = useRef<string[]>([])

  /** نوشتن مقدار تازه + بازگرداندن مکان‌نما به جای درست */
  const apply = useCallback((v: string, selStart?: number, selEnd?: number) => {
    history.current.push(ref.current?.value ?? '')
    if (history.current.length > 50) history.current.shift()
    onChange(v)
    requestAnimationFrame(() => {
      const el = ref.current
      if (!el) return
      el.focus()
      if (selStart !== undefined) el.setSelectionRange(selStart, selEnd ?? selStart)
    })
  }, [onChange])

  const tools = () => (ref.current ? new Tools(ref.current, apply) : null)

  const run = (a: Action) => {
    const t = tools()
    if (t) a.run(t)
  }

  const undo = () => {
    const prev = history.current.pop()
    if (prev !== undefined) onChange(prev)
  }

  const insertLink = () => {
    const t = tools()
    if (!t) return
    const sel = t.selected || 'متن لینک'
    const url = window.prompt('نشانی لینک (برای صفحه داخلی مثل /markets بنویسید):', 'https://')
    if (!url) return
    t.replace(`[${sel}](${url})`)
  }

  const insertTable = () => {
    const t = tools()
    if (!t) return
    t.block('| ستون اول | ستون دوم |\n| --- | --- |\n| مقدار | مقدار |')
  }

  const insertImage = (url: string, alt: string) => {
    const t = tools()
    if (!t) return
    t.block(`![${alt || 'توضیح تصویر'}](${url})`)
  }

  /** آپلود از کشیدن‌ورهاکردن یا چسباندن */
  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'))
    if (!list.length) return
    setBusy(true)
    try {
      for (const f of list) {
        const r = await uploadImage(f)
        const t = tools()
        if (t) t.block(`![${r.alt || ''}](${r.url})`)
        else onChange(`${value}\n\n![](${r.url})\n`)
      }
      toast.ok(list.length > 1 ? `${fa(list.length)} تصویر افزوده شد` : 'تصویر افزوده شد')
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setBusy(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onChange])

  /** میان‌برهای صفحه‌کلید */
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return
    const k = e.key.toLowerCase()
    if (k === 'z') { e.preventDefault(); undo(); return }
    if (k === 'k') { e.preventDefault(); insertLink(); return }
    for (const group of ACTIONS) {
      for (const a of group) {
        if (a.key === k) { e.preventDefault(); run(a); return }
      }
    }
  }

  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(e.clipboardData?.files || [])
    if (files.length) {
      e.preventDefault()
      handleFiles(files)
    }
  }

  // ارتفاع خودکار textarea تا نویسنده مجبور به اسکرول داخل کادر نباشد
  useEffect(() => {
    const el = ref.current
    if (!el || mode === 'preview') return
    el.style.height = 'auto'
    el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`
  }, [value, mode, minHeight])

  const words = value.trim() ? value.trim().split(/\s+/).length : 0

  const Btn = ({ icon: Icon, title, onClick, active }: any) => (
    <button type="button" title={title} onClick={onClick}
      className={`grid h-8 w-8 place-items-center rounded-lg transition ${
        active ? 'bg-brand-500/20 text-brand-200' : 'text-slate-400 hover:bg-white/[.07] hover:text-slate-100'
      }`}>
      <Icon className="h-4 w-4" />
    </button>
  )

  return (
    <div className="rounded-2xl border border-white/[.07] bg-ink-950/40">
      {/* ── نوار ابزار */}
      <div className="flex flex-wrap items-center gap-1 border-b border-white/[.07] p-2">
        {ACTIONS.map((group, gi) => (
          <div key={gi} className="flex items-center gap-0.5 border-l border-white/[.07] pl-1 last:border-0">
            {group.map((a) => (
              <Btn key={a.title} icon={a.icon} title={a.title} onClick={() => run(a)} />
            ))}
          </div>
        ))}
        <div className="flex items-center gap-0.5 border-l border-white/[.07] pl-1">
          <Btn icon={Link2} title="لینک  (Ctrl+K)" onClick={insertLink} />
          <Btn icon={Table} title="جدول" onClick={insertTable} />
          <Btn icon={Minus} title="خط جداکننده" onClick={() => tools()?.block('---')} />
        </div>
        <div className="flex items-center gap-0.5 border-l border-white/[.07] pl-1">
          <Btn icon={busy ? Loader2 : ImageIcon} title="افزودن تصویر" onClick={() => setPicker(true)} />
          <Btn icon={Undo2} title="بازگردانی  (Ctrl+Z)" onClick={undo} />
        </div>

        <div className="mr-auto flex items-center gap-0.5">
          <Btn icon={Pencil} title="فقط نوشتن" active={mode === 'write'} onClick={() => setMode('write')} />
          <Btn icon={Columns2} title="نوشتن و پیش‌نمایش" active={mode === 'split'} onClick={() => setMode('split')} />
          <Btn icon={Eye} title="فقط پیش‌نمایش" active={mode === 'preview'} onClick={() => setMode('preview')} />
        </div>
      </div>

      {/* ── ناحیه نوشتن / پیش‌نمایش */}
      <div className={mode === 'split' ? 'grid gap-0 lg:grid-cols-2' : ''}>
        {mode !== 'preview' && (
          <div
            className={`relative ${drag ? 'ring-2 ring-inset ring-brand-500/60' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDrag(false)
              handleFiles(e.dataTransfer.files)
            }}
          >
            <textarea
              ref={ref}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyDown}
              onPaste={onPaste}
              spellCheck={false}
              style={{ minHeight }}
              className="w-full resize-none border-0 bg-transparent p-4 text-[13px] leading-8 text-slate-200 outline-none"
              placeholder={'متن مقاله را بنویسید…\n\nتصویر را می‌توانید همین‌جا رها کنید یا بچسبانید (Ctrl+V).'}
            />
            {drag && (
              <div className="pointer-events-none absolute inset-0 grid place-items-center bg-ink-950/70">
                <div className="flex items-center gap-2 text-sm text-brand-200">
                  <Upload className="h-5 w-5" /> تصویر را اینجا رها کنید
                </div>
              </div>
            )}
            {busy && (
              <div className="absolute left-3 top-3 flex items-center gap-2 rounded-lg bg-ink-900/90 px-3 py-1.5 text-[11px] text-slate-300">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> در حال بارگذاری تصویر…
              </div>
            )}
          </div>
        )}

        {mode !== 'write' && (
          <div className={`overflow-y-auto p-4 ${mode === 'split' ? 'border-r border-white/[.07]' : ''}`}
               style={{ maxHeight: mode === 'split' ? minHeight + 80 : undefined, minHeight }}>
            {value.trim()
              ? <Markdown body={value} />
              : <p className="pt-10 text-center text-xs text-slate-600">پیش‌نمایش اینجا نشان داده می‌شود</p>}
          </div>
        )}
      </div>

      {/* ── نوار پایین */}
      <div className="flex flex-wrap items-center gap-3 border-t border-white/[.07] px-3 py-2 text-[11px] text-slate-500">
        <span>{fa(words)} کلمه</span>
        <span>·</span>
        <span>حدود {fa(Math.max(1, Math.ceil(words / 200)))} دقیقه مطالعه</span>
        <span className="mr-auto text-slate-600">
          کشیدن تصویر، چسباندن (Ctrl+V) و میان‌برها پشتیبانی می‌شوند
        </span>
      </div>

      <MediaPicker
        open={picker}
        onClose={() => setPicker(false)}
        onPick={(url, alt) => { insertImage(url, alt); setPicker(false) }}
      />
    </div>
  )
}
